import { useMemo } from "react";

/**
 * 既読表示に必要な受信者情報の最小インターフェース。
 * `DmMessage["receivers"]` などはこの形に構造的に適合する。
 */
export interface MessageReadReceiptUser {
  id: number | string;
  display_name?: string | null;
  avatar?: string | null;
  seen_at?: string | null;
}

export interface MessageReadReceiptsResult {
  /** 既読済みユーザー（自分のメッセージの場合は自分を除外）。 */
  readReceiptUsers: MessageReadReceiptUser[];
  /** 自分のメッセージで最も新しい既読日時（無ければ null）。 */
  latestSeenAt: string | null;
}

/**
 * メッセージの既読情報を算出する共通フック。
 *
 * 通常メッセージ（`MessageItem`）と契約書アイテム（`ContractTimelineItem`）で
 * 既読アイコン・既読日時の表示ロジックを共通化するために切り出した。
 *
 * BE 仕様: `receivers` は「そのメッセージの受信者（参加者）リスト」で、実際に
 * 既読したユーザーのみ `seen_at` に時刻が入る。未読ユーザーは除外する。
 */
export const useMessageReadReceipts = (
  receivers: MessageReadReceiptUser[] | undefined,
  isOurs: boolean,
  currentUserId?: number | string,
): MessageReadReceiptsResult => {
  const readReceiptUsers = useMemo(() => {
    if (!receivers || receivers.length === 0) {
      return [];
    }
    const readUsers = receivers.filter((user) => !!user.seen_at);
    // 自分のメッセージ: 自分以外の既読ユーザーを表示
    // 相手のメッセージ: すべての既読ユーザー（自分を含む）を表示
    if (isOurs && currentUserId) {
      return readUsers.filter((user) => user.id !== currentUserId);
    }
    return readUsers;
  }, [receivers, isOurs, currentUserId]);

  const latestSeenAt = useMemo(() => {
    if (!isOurs || readReceiptUsers.length === 0) return null;
    const seenAts = readReceiptUsers
      .map((u) => u.seen_at)
      .filter((date): date is string => !!date);
    if (seenAts.length === 0) return null;
    return seenAts.sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    )[0];
  }, [isOurs, readReceiptUsers]);

  return { readReceiptUsers, latestSeenAt };
};
